import { Checkbox } from '@restomarker/ui-kit';
import type { ReactElement } from 'react';

import { ru, t, type MessageKey } from '@/shared/i18n';

import type { AlertTier } from './alertSettingsTypes';
import styles from './alertTiersField.module.css';

/** «За половину срока (не раньше чем за сутки)» — единственный порог не «за N до конца». */
const HALF: AlertTier = 'expiring';

export interface AlertTiersFieldProps {
  /** Отмеченные пороги канала — как их хранит сервер. */
  value: readonly AlertTier[];
  /** Пороги, которые знает сервер, в его каноническом порядке (`available_tiers`). */
  available: readonly AlertTier[];
  disabled?: boolean;
  /** Полный новый набор канала, в порядке сервера. */
  onChange: (next: AlertTier[]) => void;
  /**
   * Подпись сетки. Разная у личных порогов и порогов точки (№12, 2026-09-30): две сетки
   * «Когда предупреждать» подряд владелец принял за задублированное окно.
   */
  legend?: string;
}

/**
 * Когда предупреждать — свой набор порогов у канала (T-046, решение владельца 2026-09-29):
 * «за половину срока», за 30 мин, 1, 2 … 6 ч, галочками, несколько сразу.
 *
 * Готовый `Checkbox` ui-kit, а не новый компонент: строка не ниже `--ds-control-min`
 * (в киоске 48px — под палец в перчатке) и отметка ink. Цвета нет — он отдан статусу срока.
 *
 * Последнюю отмеченную галочку снять нельзя — так решил владелец: чтобы не получать
 * предупреждения, выключают сам канал. Галочка при этом не молчит: под сеткой сказано,
 * почему она не снимается.
 *
 * Подпись берётся по коду с сервера (`alerts.tier.<код>`). Код, которого бандл не знает,
 * не рисуется — но и не теряется: он остаётся в наборе и уходит серверу обратно.
 */
export function AlertTiersField({
  value,
  available,
  disabled = false,
  onChange,
  legend = t('alerts.when'),
}: AlertTiersFieldProps): ReactElement {
  const shown = available.filter((tier) => labelOf(tier) !== null);
  const leads = shown.filter((tier) => tier !== HALF);
  const lastOne = value.length === 1;

  function box(tier: AlertTier, hint?: string): ReactElement {
    const checked = value.includes(tier);
    return (
      <Checkbox
        key={tier}
        checked={checked}
        disabled={disabled || (checked && lastOne)}
        onCheckedChange={(next) => onChange(nextTiers(value, available, tier, next))}
        label={labelOf(tier)}
        hint={hint}
      />
    );
  }

  return (
    <fieldset className={styles.field}>
      <legend className={styles.legend}>{legend}</legend>
      {shown.includes(HALF) ? box(HALF, t('alerts.halfHint')) : null}
      {leads.length > 0 ? (
        <>
          <p className={styles.leads}>{t('alerts.leads')}</p>
          <div className={styles.grid}>{leads.map((tier) => box(tier))}</div>
        </>
      ) : null}
      {lastOne ? <p className={styles.note}>{t('alerts.lastOne')}</p> : null}
      <p className={styles.note}>{t('alerts.whenHint')}</p>
    </fieldset>
  );
}

function labelOf(tier: AlertTier): string | null {
  const key = `alerts.tier.${tier}`;
  return isMessageKey(key) ? t(key) : null;
}

function isMessageKey(key: string): key is MessageKey {
  return Object.hasOwn(ru, key);
}

/**
 * Новый набор: переключённый порог плюс остальные отмеченные — в порядке сервера.
 *
 * Отбор по `available`, а не по тому, что нарисовано: код, неизвестный бандлу, но
 * известный серверу, сохраняется. Код, которого сервер больше не знает, отпадает —
 * иначе каждое сохранение получало бы 400 «неизвестный порог».
 */
function nextTiers(
  value: readonly AlertTier[],
  available: readonly AlertTier[],
  tier: AlertTier,
  checked: boolean,
): AlertTier[] {
  const chosen = new Set(value);
  if (checked) chosen.add(tier);
  else chosen.delete(tier);
  return available.filter((code) => chosen.has(code));
}
